import { useState, useEffect } from "react";
import axios from "axios";
import { Trophy, TrendingUp, MousePointer, DollarSign } from "lucide-react";
import { motion } from "framer-motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Leaderboard() {
  const [leaders, setLeaders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/leaderboard`);
        setLeaders(res.data);
      } catch (err) {
        console.error("Leaderboard error:", err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const getRankStyle = (rank) => {
    if (rank === 1) return { bg: "bg-[#E31937]/10", border: "border-[#E31937]", badge: "bg-[#E31937]", text: "text-[#E31937]" };
    if (rank === 2) return { bg: "bg-[#00BFFF]/10", border: "border-[#00BFFF]", badge: "bg-[#00BFFF]", text: "text-[#00BFFF]" };
    if (rank === 3) return { bg: "bg-[#F59E0B]/10", border: "border-[#F59E0B]", badge: "bg-[#F59E0B]", text: "text-[#F59E0B]" };
    return { bg: "bg-[#141417]", border: "border-[#2A2A30]", badge: "bg-[#2A2A30]", text: "text-slate-400" };
  };

  return (
    <div data-testid="leaderboard-page" className="min-h-screen pt-20 pb-12 px-6 md:px-12" style={{ background: '#0A0A0C' }}>
      <div className="max-w-4xl mx-auto">
        <div className="mb-10">
          <span className="text-xs tracking-[0.2em] uppercase text-[#F59E0B] font-semibold">COMMUNITY</span>
          <h1 className="text-3xl sm:text-4xl font-bold text-white tracking-tight mt-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Top Affiliates
          </h1>
          <p className="text-base text-slate-400 mt-3">See who's leading the CTCSB community in referrals and engagement.</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-2 border-[#E31937] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : leaders.length === 0 ? (
          <div className="bg-[#141417] border border-[#2A2A30] p-12 text-center">
            <Trophy className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <h3 className="text-lg font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Leaderboard Empty</h3>
            <p className="text-sm text-slate-400">Be the first to climb the ranks! Start sharing your affiliate links.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {leaders.map((leader, i) => {
              const style = getRankStyle(leader.rank);
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.05 }}
                  data-testid={`leaderboard-rank-${leader.rank}`}
                  className={`flex items-center gap-5 p-5 border ${style.border} ${style.bg} transition-all duration-300`}
                >
                  <div className={`w-10 h-10 ${style.badge} flex items-center justify-center font-bold text-white text-sm`}
                    style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                    {leader.rank}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-white text-sm truncate">{leader.name}</h3>
                  </div>
                  <div className="hidden md:flex items-center gap-8 text-center">
                    <div>
                      <div className="flex items-center gap-1 text-xs text-slate-500 mb-1">
                        <TrendingUp className="w-3 h-3" /> Referrals
                      </div>
                      <div className="text-lg font-bold font-mono text-white">{leader.referral_count}</div>
                    </div>
                    <div>
                      <div className="flex items-center gap-1 text-xs text-slate-500 mb-1">
                        <MousePointer className="w-3 h-3" /> Clicks
                      </div>
                      <div className="text-lg font-bold font-mono text-[#00BFFF]">{leader.total_clicks}</div>
                    </div>
                    <div>
                      <div className="flex items-center gap-1 text-xs text-slate-500 mb-1">
                        <DollarSign className="w-3 h-3" /> Earned
                      </div>
                      <div className="text-lg font-bold font-mono text-[#22C55E]">${(leader.total_earnings || 0).toFixed(2)}</div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
